import { useCallback, useEffect, useRef, useState } from 'react';

interface GetNextListIndexParams {
  key: string;
  currentIndex: number;
  itemCount: number;
  wrap: boolean;
}

type KeyEventLike = {
  key: string;
  preventDefault: () => void;
};

interface UseListKeyboardNavigationParams {
  itemCount: number;
  wrap?: boolean;
  initialIndex?: number;
  onEnter?: (selectedIndex: number) => void;
  onEscape?: () => void;
  focusSelectedItem?: boolean;
  scrollSelectedItemIntoView?: boolean;
}

export const getNextListIndex = ({
  key,
  currentIndex,
  itemCount,
  wrap,
}: GetNextListIndexParams): number | null => {
  if (key !== 'ArrowDown' && key !== 'ArrowUp') {
    return null;
  }

  if (itemCount <= 0) {
    return -1;
  }

  if (currentIndex < 0 || currentIndex >= itemCount) {
    return key === 'ArrowDown' ? 0 : itemCount - 1;
  }

  const step = key === 'ArrowDown' ? 1 : -1;
  const nextIndex = currentIndex + step;

  if (wrap) {
    return (nextIndex + itemCount) % itemCount;
  }

  if (nextIndex < 0) {
    return 0;
  }

  if (nextIndex >= itemCount) {
    return itemCount - 1;
  }

  return nextIndex;
};

export const useListKeyboardNavigation = ({
  itemCount,
  wrap = false,
  initialIndex = 0,
  onEnter,
  onEscape,
  focusSelectedItem = false,
  scrollSelectedItemIntoView = true,
}: UseListKeyboardNavigationParams) => {
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  const registerItemRef = useCallback(
    (index: number) => (node: HTMLElement | null) => {
      itemRefs.current[index] = node;
    },
    [],
  );

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, itemCount);
  }, [itemCount]);

  useEffect(() => {
    if (itemCount <= 0) {
      setSelectedIndex(initialIndex);
      return;
    }

    if (selectedIndex >= itemCount) {
      setSelectedIndex(itemCount - 1);
    }
  }, [initialIndex, itemCount, selectedIndex]);

  useEffect(() => {
    if (itemCount <= 0 || selectedIndex < 0 || selectedIndex >= itemCount) {
      return;
    }

    const selectedNode = itemRefs.current[selectedIndex];
    if (!selectedNode) {
      return;
    }

    if (scrollSelectedItemIntoView && typeof selectedNode.scrollIntoView === 'function') {
      selectedNode.scrollIntoView({ block: 'nearest' });
    }

    if (focusSelectedItem && typeof selectedNode.focus === 'function') {
      selectedNode.focus();
    }
  }, [focusSelectedItem, itemCount, scrollSelectedItemIntoView, selectedIndex]);

  const handleKeyDown = useCallback(
    (event: KeyEventLike): boolean => {
      const nextIndex = getNextListIndex({
        key: event.key,
        currentIndex: selectedIndex,
        itemCount,
        wrap,
      });

      if (nextIndex !== null) {
        event.preventDefault();
        if (nextIndex >= 0) {
          setSelectedIndex(nextIndex);
        }
        return true;
      }

      if (event.key === 'Enter') {
        if (onEnter) {
          event.preventDefault();
          if (itemCount > 0) {
            onEnter(selectedIndex);
          }
          return true;
        }
        return false;
      }

      if (event.key === 'Escape' && onEscape) {
        event.preventDefault();
        onEscape();
        return true;
      }

      return false;
    },
    [itemCount, onEnter, onEscape, selectedIndex, wrap],
  );

  return {
    selectedIndex,
    setSelectedIndex,
    registerItemRef,
    handleKeyDown,
  };
};
